import { Box, ButtonBase, Fade, Stack, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { Block, WebBackButton } from "../../components/shared";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTelegram } from "../../hooks";

const WEBSITE_URL =
  process.env.REACT_APP_WEBSITE_URL || "https://zero-wallet.net";
const SUPPORT_URL =
  process.env.REACT_APP_SUPPORT_URL || "https://t.me/zero_wallet_support";
const TELEGRAM_URL =
  process.env.REACT_APP_TELEGRAM_CHANNEL_URL ||
  "https://t.me/ZeroWalletChannel";

const APP_VERSION = process.env.REACT_APP_VERSION || "0.1.0";

interface FeatureItem {
  icon: string;
  title: string;
  desc: string;
}

interface LinkItem {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

export function AboutPage() {
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { t } = useTranslation("about");

  const FEATURES: FeatureItem[] = [
    {
      icon: "solar:qr-code-linear",
      title: t("features.qr.title"),
      desc: t("features.qr.desc"),
    },
    {
      icon: "solar:card-2-linear",
      title: t("features.cards.title"),
      desc: t("features.cards.desc"),
    },
    {
      icon: "solar:refresh-circle-linear",
      title: t("features.swap.title"),
      desc: t("features.swap.desc"),
    },
    {
      icon: "solar:shield-keyhole-linear",
      title: t("features.non_custodial.title"),
      desc: t("features.non_custodial.desc"),
    },
  ];
  useEffect(() => {
    const handler = () => navigate("/profile");
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handler);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handler);
    };
  }, [navigate, tg]);

  const LINKS: LinkItem[] = [
    {
      icon: "solar:global-linear",
      title: t("links.site.title"),
      subtitle: WEBSITE_URL.replace(/^https?:\/\//, ""),
      onClick: () => Telegram.WebApp.openLink(WEBSITE_URL),
    },
    {
      icon: "mdi:telegram",
      title: t("links.channel.title"),
      subtitle: t("links.channel.subtitle"),
      onClick: () => Telegram.WebApp.openTelegramLink(TELEGRAM_URL),
    },
    {
      icon: "solar:chat-round-dots-linear",
      title: t("links.support.title"),
      subtitle: t("links.support.subtitle"),
      onClick: () => Telegram.WebApp.openTelegramLink(SUPPORT_URL),
    },
  ];

  return (
    <Fade in>
      <Box sx={{ marginBottom: "100px", width: "100%" }}>
        <WebBackButton to="/profile" title={t("title")} />

        {/* Hero: логотип + короткая суть проекта */}
        <Block
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              backgroundColor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon
              icon="solar:wallet-money-bold"
              width={26}
              height={26}
              color="#fff"
            />
          </Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
            Zero Wallet
          </Typography>
          <Typography sx={{ fontSize: "13px", opacity: 0.7, lineHeight: 1.5 }}>
            {t("hero.description")}
          </Typography>
        </Block>

        {/* Возможности */}
        <Box sx={{ marginTop: "14px" }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              opacity: 0.55,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              paddingLeft: "4px",
              marginBottom: "8px",
            }}
          >
            {t("sections.features")}
          </Typography>
          <Block sx={{ padding: "8px 14px" }}>
            <Stack divider={<Divider />} spacing={0}>
              {FEATURES.map((f) => (
                <Box
                  key={f.title}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "12px 0",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: "10px",
                      backgroundColor: (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(51,119,255,0.14)"
                          : "rgba(51,119,255,0.10)",
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon icon={f.icon} width={20} height={20} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                      {f.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        opacity: 0.6,
                        marginTop: "2px",
                        lineHeight: 1.4,
                      }}
                    >
                      {f.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Block>
        </Box>

        {/* Ссылки: сайт, канал, поддержка */}
        <Box sx={{ marginTop: "14px" }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              opacity: 0.55,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              paddingLeft: "4px",
              marginBottom: "8px",
            }}
          >
            {t("sections.contact")}
          </Typography>
          <Block sx={{ padding: "6px 14px" }}>
            <Stack divider={<Divider />} spacing={0}>
              {LINKS.map((l) => (
                <ButtonBase
                  key={l.title}
                  onClick={l.onClick}
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 0",
                    textAlign: "left",
                    "&:active": { transform: "scale(0.995)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon icon={l.icon} width={18} height={18} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                      {l.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11.5px",
                        opacity: 0.55,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {l.subtitle}
                    </Typography>
                  </Box>
                  <Icon
                    icon="solar:alt-arrow-right-linear"
                    width={18}
                    height={18}
                    color="#b0b0b0"
                  />
                </ButtonBase>
              ))}
            </Stack>
          </Block>
        </Box>

        <Typography
          sx={{
            textAlign: "center",
            fontSize: "11px",
            opacity: 0.5,
            marginTop: "22px",
            letterSpacing: "0.4px",
          }}
        >
          {t("version", { version: APP_VERSION })}
        </Typography>
      </Box>
    </Fade>
  );
}

function Divider() {
  return (
    <Box
      sx={{
        height: "1px",
        backgroundColor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.05)",
      }}
    />
  );
}
